'use client';

import { useState, type FormEvent } from 'react';
import { HoneypotField } from '@/components/honeypot-field';
import { FormFallbackFields } from '@/components/form-fallback-fields';
import { DEFAULT_SUCCESS_PATH } from '@/lib/forms/form-transport';
import { HONEYPOT_FIELD } from '@/lib/forms/spam';
import { readTrackingPayload } from '@/lib/forms/tracking-payload';
import { EVENT_TIMING_OPTIONS } from '@/lib/forms/ebook';
import { EBOOK_FORM_ENDPOINT } from '@/lib/forms/lead-form-variants';
import { FIELD_MAX } from '@/lib/forms/field-limits';
import { validateLeadForm, type LeadFormRules } from '@/lib/forms/lead-form-validation';
import { describeSubmitFailure, type FormErrorBody, type SubmitFailure } from '@/lib/forms/submit-failure';
import { goToSuccessPage } from '@/lib/forms/success-navigation';
import {
  EBOOK_FORM_VARIANT,
  FORM_EVENT,
  FORM_FAILURE_REASON,
  failureReasonForStatus,
  trackFormEvent,
} from '@/lib/analytics/posthog';

const FIELD_BASE =
  'w-full rounded-lg border border-brand-navy/15 bg-white px-3.5 py-2.5 text-[15px] text-brand-navy placeholder:text-brand-navy/70 transition-shadow outline-none focus:border-brand-blue focus:ring-4 focus:ring-brand-blue/10';

const ERROR_RING = ' !border-red-500 ring-2 ring-red-500/25';

/** The gate asks for the contact form's identity fields and nothing else. */
const EBOOK_RULES: LeadFormRules = { compact: false, eventFields: false, messageRequired: false, messageLabel: '' };

/** The gate's text fields, top to bottom: name, placeholder label, autocomplete, max length. */
const EBOOK_FIELDS = [
  { name: 'firstName', label: 'First name', autoComplete: 'given-name', maxLength: FIELD_MAX.name },
  { name: 'lastName', label: 'Last name', autoComplete: 'family-name', maxLength: FIELD_MAX.name },
  { name: 'email', label: 'Email', autoComplete: 'email', maxLength: FIELD_MAX.email, type: 'email' },
  { name: 'company', label: 'Company', autoComplete: 'organization', maxLength: FIELD_MAX.company },
] as const;

type EbookFieldName = (typeof EBOOK_FIELDS)[number]['name'];

/** Light gate for an eBook download; confirms inline instead of redirecting. */
export function EbookForm({
  ebookSlug,
  pdfUrl,
  successPath,
  askEventTiming = false,
}: {
  ebookSlug: string;
  pdfUrl?: string;
  successPath?: string;
  /** BEST Moves pages ask when their next event is, so sales knows how to open. */
  askEventTiming?: boolean;
}) {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const clearFieldError = (name: string) =>
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      return Object.fromEntries(Object.entries(prev).filter(([key]) => key !== name));
    });

  function focusFirstInvalid(form: HTMLFormElement, errors: Record<string, string>) {
    const first = Object.keys(errors)[0];
    const el = first ? form.elements.namedItem(first) : null;
    if (el instanceof HTMLElement) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus({ preventScroll: true });
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    trackFormEvent(FORM_EVENT.attempt, { variant: EBOOK_FORM_VARIANT });
    const form = event.currentTarget;
    const data = new FormData(form);
    const text = (name: string) => {
      const value = data.get(name);
      return typeof value === 'string' ? value : '';
    };

    const errors = validateLeadForm(
      Object.fromEntries(EBOOK_FIELDS.map(({ name }) => [name, text(name)])),
      EBOOK_RULES,
    );
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      trackFormEvent(FORM_EVENT.failed, { variant: EBOOK_FORM_VARIANT, reason: FORM_FAILURE_REASON.clientValidation });
      setStatus('idle');
      focusFirstInvalid(form, errors);
      return;
    }
    setStatus('submitting');
    setErrorMessage(undefined);

    const payload: Record<string, unknown> = {
      firstName: text('firstName'),
      lastName: text('lastName'),
      email: text('email'),
      company: text('company'),
      ebook: ebookSlug,
      ...(askEventTiming ? { eventTiming: data.get('eventTiming') } : {}),
      [HONEYPOT_FIELD]: data.get(HONEYPOT_FIELD) || undefined,
      tracking: readTrackingPayload(),
    };
    let failure: SubmitFailure;
    try {
      const response = await fetch(EBOOK_FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        trackFormEvent(FORM_EVENT.succeeded, { variant: EBOOK_FORM_VARIANT });
        if (successPath) {
          goToSuccessPage(successPath);
          return;
        }
        setStatus('done');
        return;
      }
      const body = (await response.json().catch(() => null)) as FormErrorBody | null;
      failure = { kind: 'response', status: response.status, body };
    } catch {
      failure = { kind: 'network' };
    }
    trackFormEvent(FORM_EVENT.failed, {
      variant: EBOOK_FORM_VARIANT,
      reason: failure.kind === 'network' ? FORM_FAILURE_REASON.network : failureReasonForStatus(failure.status),
    });
    const view = describeSubmitFailure(failure, {
      values: payload,
      compact: false,
      hasControl: (name) => EBOOK_FIELDS.some((field) => field.name === name),
    });
    setFieldErrors(view.fieldErrors);
    setStatus('error');
    setErrorMessage(view.message);
    focusFirstInvalid(form, view.fieldErrors);
  }

  const textInput = (name: EbookFieldName) => {
    const field = EBOOK_FIELDS.find((candidate) => candidate.name === name);
    if (!field) return null;
    const error = fieldErrors[name];
    const errorId = `ebook-${name}-error`;
    return (
      <div className="flex flex-col gap-1">
        <input
          name={name}
          type={'type' in field ? field.type : 'text'}
          required
          maxLength={field.maxLength}
          aria-label={field.label}
          placeholder={`${field.label} *`}
          autoComplete={field.autoComplete}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onInput={() => clearFieldError(name)}
          className={`${FIELD_BASE}${error ? ERROR_RING : ''}`}
        />
        {error ? (
          <p id={errorId} className="text-xs font-semibold text-red-600">
            {error}
          </p>
        ) : null}
      </div>
    );
  };

  if (status === 'done') {
    return (
      <div className="rounded-xl bg-brand-navy/5 p-6 text-center">
        <p className="text-lg font-bold text-brand-navy">Enjoy your eBook! 🎉</p>
        {pdfUrl ? (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="mt-4 inline-block rounded-full bg-brand-yellow px-7 py-3 font-bold text-brand-navy shadow-sm transition-all hover:-translate-y-px hover:bg-brand-yellow-dark"
          >
            Download the PDF
          </a>
        ) : (
          <p className="mt-2 text-sm text-brand-navy/70">
            Your download link is on its way to the email you entered.
          </p>
        )}
      </div>
    );
  }

  return (
    <form action={EBOOK_FORM_ENDPOINT} method="post" onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <FormFallbackFields next={successPath ?? DEFAULT_SUCCESS_PATH['ebook'] ?? '/'} />
      <input type="hidden" name="ebook" value={ebookSlug} />
      <div className="grid gap-4 sm:grid-cols-2">
        {textInput('firstName')}
        {textInput('lastName')}
      </div>
      {textInput('email')}
      {textInput('company')}
      {askEventTiming ? (
        <div className="rounded-xl bg-brand-mist px-4 py-3.5">
          <label htmlFor="eventTiming" className="block text-[13px] font-bold text-brand-navy">
            When is your next team event?
          </label>
          <select
            id="eventTiming"
            name="eventTiming"
            defaultValue="Just researching for now"
            className={`${FIELD_BASE} mt-1.5`}
          >
            {EVENT_TIMING_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <p className="mt-2 text-xs text-brand-navy/70">
            This is the one that matters. It tells us whether to call this week or leave you alone
            until you are ready.
          </p>
        </div>
      ) : null}
      <HoneypotField />
      {status === 'error' ? (
        <p role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          {errorMessage}
        </p>
      ) : null}
      <button
        type="submit"
        disabled={status === 'submitting'}
        className="rounded-full bg-brand-yellow px-8 py-3.5 text-[15px] font-bold text-brand-navy shadow-sm transition-all hover:-translate-y-px hover:bg-brand-yellow-dark hover:shadow-md disabled:translate-y-0 disabled:opacity-60"
      >
        {status === 'submitting' ? 'Sending…' : 'Get the free eBook'}
      </button>
    </form>
  );
}
